/**
 * Canonical post identity (FR-SUB-001/002, SRS 16.1, architecture §10).
 *
 * A submitted URL is *parsed*, never fetched. These adapters are pure
 * functions over the platform's URL grammar: they accept `https` only, an
 * exact host allowlist per platform, only the documented URL shapes, and
 * they return the canonical post id — the value the global uniqueness
 * index and the measurement pipeline both key on. Anything else is
 * refused, and a refusal names which rule it broke.
 *
 * Two properties matter more than the parsing.
 *
 * **Every variant of one post resolves to one id.** Tracking parameters,
 * mobile hosts, `/shorts/` versus `/watch?v=`, a trailing slash, a
 * different `@handle` in a TikTok path — all of it collapses. That is what
 * makes `UNIQUE (platform, post_id)` mean "one payable assignment per
 * post" rather than "per URL someone happened to paste" (TC-009).
 *
 * **Nothing here resolves a short link.** `youtu.be`, `vm.tiktok.com` and
 * `vt.tiktok.com` need an HTTP round trip to learn the real id, and doing
 * that in the request path would let a submitted URL make a Worker call
 * out. They return `needsResolution`, and `cm-async` follows them with a
 * hop limit, the allowlist re-checked on every hop, and no credentials.
 *
 * The adapters are deliberately independent of who reads the metrics.
 * Canonicalization is a property of the platform's URL grammar, so these
 * survive the move to or from Zernio unchanged.
 */

export type Platform = "youtube" | "tiktok";
export type SubmitPlatform = Platform | "instagram" | "facebook" | "reddit";

export interface CanonicalPost {
  platform: Platform;
  /** The platform's own stable id for the post. */
  postId: string;
  /** The URL this platform would consider canonical for that id. */
  canonicalUrl: string;
}

export interface NeedsResolution {
  platform: Platform;
  /** The short link, to be followed by cm-async and never in a request. */
  shortUrl: string;
  needsResolution: true;
}

export type CanonicalizeResult =
  | { ok: true; post: CanonicalPost }
  | { ok: true; pending: NeedsResolution }
  | { ok: false; reason: string };

/**
 * Every host any adapter will accept, for the outbound-allowlist CI test
 * and for the short-link resolver in `cm-async`.
 */
export const ALLOWED_HOSTS: Readonly<Record<Platform, readonly string[]>> = {
  youtube: ["www.youtube.com", "youtube.com", "m.youtube.com", "youtu.be"],
  tiktok: ["www.tiktok.com", "tiktok.com", "m.tiktok.com", "vm.tiktok.com", "vt.tiktok.com"],
};

/** Hosts a creator may submit for brand review even when we cannot measure them. */
export const REVIEW_HOSTS: Readonly<Record<Exclude<SubmitPlatform, Platform>, readonly string[]>> = {
  instagram: ["www.instagram.com", "instagram.com", "m.instagram.com"],
  facebook: ["www.facebook.com", "facebook.com", "m.facebook.com", "fb.com", "www.fb.com"],
  reddit: ["www.reddit.com", "reddit.com", "old.reddit.com", "m.reddit.com"],
};

/** Hosts whose links carry no id and must be followed to learn one. */
export const SHORT_LINK_HOSTS: ReadonlySet<string> = new Set([
  "youtu.be", "vm.tiktok.com", "vt.tiktok.com",
]);

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const TIKTOK_ID = /^\d{5,25}$/;

/**
 * Parses a URL string without letting a malformed one throw.
 *
 * A submission is user input; `new URL()` on user input is an exception
 * waiting to be a 500.
 */
function parse(raw: string): URL | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > 2048) return null;
  try {
    return new URL(raw.trim());
  } catch {
    return null;
  }
}

function hostOf(url: URL): string {
  // Strip a trailing dot (the root label) so "youtube.com." cannot slip
  // past an exact-match allowlist.
  return url.hostname.toLowerCase().replace(/\.$/, "");
}

/** Which platform claims this host, if any. */
export function platformOf(url: URL): Platform | null {
  const host = hostOf(url);
  for (const [platform, hosts] of Object.entries(ALLOWED_HOSTS)) {
    if (hosts.includes(host)) return platform as Platform;
  }
  return null;
}

/* ---------------------------------------------------------------- youtube */

function youtube(url: URL): CanonicalizeResult {
  const host = hostOf(url);
  const segments = url.pathname.split("/").filter(Boolean);

  if (host === "youtu.be") {
    // youtu.be/<id> carries the id, so it does not actually need a round
    // trip — but only when the segment is a well-formed id. Anything else
    // on that host is a link shortener result we cannot read.
    const id = segments[0];
    if (id && YOUTUBE_ID.test(id)) return okPost("youtube", id);
    return { ok: true, pending: { platform: "youtube", shortUrl: url.toString(), needsResolution: true } };
  }

  // /watch?v=<id>
  if (segments[0] === "watch") {
    const id = url.searchParams.get("v");
    if (id && YOUTUBE_ID.test(id)) return okPost("youtube", id);
    return { ok: false, reason: "That YouTube watch URL has no video id in it." };
  }

  // /shorts/<id>, /embed/<id>, /live/<id>, /v/<id>
  if (segments.length >= 2 && ["shorts", "embed", "live", "v"].includes(segments[0]!)) {
    const id = segments[1]!;
    if (YOUTUBE_ID.test(id)) return okPost("youtube", id);
    return { ok: false, reason: "That does not look like a YouTube video id." };
  }

  // A channel, a playlist, a search — real YouTube URLs, but not a post.
  return { ok: false, reason: "That is a YouTube link, but not to a single video." };
}

/* ----------------------------------------------------------------- tiktok */

function tiktok(url: URL): CanonicalizeResult {
  const host = hostOf(url);
  const segments = url.pathname.split("/").filter(Boolean);

  if (host === "vm.tiktok.com" || host === "vt.tiktok.com") {
    return { ok: true, pending: { platform: "tiktok", shortUrl: url.toString(), needsResolution: true } };
  }

  // /t/<code> is a short link on the main host.
  if (segments[0] === "t") {
    return { ok: true, pending: { platform: "tiktok", shortUrl: url.toString(), needsResolution: true } };
  }

  // /@handle/video/<id> — the handle is display only and is dropped, so a
  // creator renaming themselves does not change the post's identity.
  if (segments.length >= 3 && segments[0]!.startsWith("@") && segments[1] === "video") {
    const id = segments[2]!;
    if (TIKTOK_ID.test(id)) return okPost("tiktok", id);
    return { ok: false, reason: "That does not look like a TikTok video id." };
  }

  // /video/<id> without the handle, which TikTok also serves.
  if (segments.length >= 2 && segments[0] === "video") {
    const id = segments[1]!;
    if (TIKTOK_ID.test(id)) return okPost("tiktok", id);
    return { ok: false, reason: "That does not look like a TikTok video id." };
  }

  return { ok: false, reason: "That is a TikTok link, but not to a single video." };
}

/* ------------------------------------------------------------------- api */

function okPost(platform: Platform, postId: string): CanonicalizeResult {
  return { ok: true, post: { platform, postId, canonicalUrl: canonicalUrl(platform, postId) } };
}

/**
 * The one URL shape this platform's post id is stored as.
 *
 * TikTok identity stays `/video/{id}` so a renamed handle does not become
 * a new post. That path 404s in a browser. Open links use `postOpenUrl`.
 */
export function canonicalUrl(platform: Platform, postId: string): string {
  return platform === "youtube"
    ? `https://www.youtube.com/watch?v=${postId}`
    : `https://www.tiktok.com/video/${postId}`;
}

const TIKTOK_WITH_HANDLE = /tiktok\.com\/@([^/?#]+)\/video\/(\d+)/i;
const TIKTOK_VIDEO_URL = /tiktok\.com\/(?:@[^/?#]+\/)?video\/(\d+)/i;

/**
 * A TikTok URL a browser can open. Prefer the submitted `@handle/video/id`
 * link. Otherwise build one from the connected account handle. The stored
 * canonical path without a handle is only the last resort.
 */
export function postOpenUrl(opts: {
  platform: string;
  postId?: string | null;
  submittedUrl?: string | null;
  canonicalUrl?: string | null;
  handle?: string | null;
}): string {
  const submitted = opts.submittedUrl?.trim() ?? "";
  const canonical = opts.canonicalUrl?.trim() ?? "";
  if (opts.platform !== "tiktok") return canonical || submitted;

  const fromSubmitted = TIKTOK_WITH_HANDLE.exec(submitted);
  if (fromSubmitted?.[1] && fromSubmitted[2]) {
    return `https://www.tiktok.com/@${fromSubmitted[1]}/video/${fromSubmitted[2]}`;
  }
  const id = (opts.postId && /^\d+$/.test(opts.postId) ? opts.postId : null)
    ?? TIKTOK_VIDEO_URL.exec(submitted)?.[1]
    ?? TIKTOK_VIDEO_URL.exec(canonical)?.[1]
    ?? null;
  const handle = (opts.handle ?? "").replace(/^@/, "").trim();
  if (id && handle && !handle.includes("/")) {
    return `https://www.tiktok.com/@${handle}/video/${id}`;
  }
  return canonical || submitted;
}

/**
 * Turns a submitted URL into a canonical post identity, or says why not.
 *
 * `allowed` is the set of routes enabled at the platform level
 * (`measurement.enabled_routes`, FR-MET-001). A URL for a platform that is
 * off reads "not supported" rather than "coming soon" — the SRS is
 * explicit that a route which cannot be measured is never advertised as
 * one that will be.
 */
export function canonicalize(
  raw: string, allowed?: readonly string[],
): CanonicalizeResult {
  const url = parse(raw);
  if (!url) return { ok: false, reason: "That is not a URL." };

  if (url.protocol !== "https:") {
    return { ok: false, reason: "The link must start with https://." };
  }
  // A URL carrying credentials, a port or a fragment is not a post link,
  // and each is a way to smuggle something past a host check.
  if (url.username || url.password) {
    return { ok: false, reason: "Remove the credentials from the link." };
  }
  if (url.port) {
    return { ok: false, reason: "Remove the port from the link." };
  }

  const platform = platformOf(url);
  if (!platform) {
    return { ok: false, reason: `We cannot measure links from ${hostOf(url)}.` };
  }
  if (allowed && !allowed.includes(platform)) {
    return { ok: false, reason: `${platform} is not a supported measurement route.` };
  }

  return platform === "youtube" ? youtube(url) : tiktok(url);
}

export type ParsedSubmission =
  | { ok: true; platform: SubmitPlatform; postId: string | null; canonicalUrl: string | null }
  | { ok: false; reason: string };

function reviewPlatformOf(url: URL): Exclude<SubmitPlatform, Platform> | null {
  const host = hostOf(url);
  for (const [platform, hosts] of Object.entries(REVIEW_HOSTS)) {
    if (hosts.includes(host)) return platform as Exclude<SubmitPlatform, Platform>;
  }
  return null;
}

function reviewIdentity(url: URL): { postId: string; canonicalUrl: string } {
  const path = url.pathname.replace(/\/+$/, "") || "/";
  const key = `${hostOf(url)}${path}${url.search}`;
  return { postId: `rev:${key}`.slice(0, 240), canonicalUrl: url.toString() };
}

/**
 * Identity for a creator-submitted URL.
 *
 * YouTube and TikTok still canonicalize to a post id when the grammar
 * has one. Instagram, Facebook and Reddit are accepted for brand review
 * even though we cannot measure them: the brand sees the connected
 * account and the pasted URL.
 */
export function parseSubmissionUrl(raw: string): ParsedSubmission {
  const parsed = canonicalize(raw);
  if (parsed.ok) {
    if ("post" in parsed) {
      return {
        ok: true,
        platform: parsed.post.platform,
        postId: parsed.post.postId,
        canonicalUrl: parsed.post.canonicalUrl,
      };
    }
    return {
      ok: true,
      platform: parsed.pending.platform,
      postId: null,
      canonicalUrl: null,
    };
  }

  const url = parse(raw);
  if (!url) return { ok: false, reason: parsed.reason };
  if (url.protocol !== "https:") return { ok: false, reason: parsed.reason };
  const review = reviewPlatformOf(url);
  if (!review) return { ok: false, reason: parsed.reason };
  const identity = reviewIdentity(url);
  return { ok: true, platform: review, ...identity };
}
